'use client';

import { useCallback, useEffect } from 'react';

import type { LogParameters } from '@/utils/logger';

type KlaviyoAPI = {
  track: (eventName: string, payload?: LogParameters) => void;
  identify: (payload?: LogParameters) => void;
};

declare global {
  interface Window {
    klaviyo?: KlaviyoAPI;
  }
}

/**
 * Hook for Klaviyo tracking with queue management
 *
 * @note Queue System: Events are queued when Klaviyo script hasn't loaded yet.
 * Once loaded, all queued events are automatically flushed via Object.defineProperty setter.
 *
 * @note Performance: Uses Object.defineProperty instead of polling for efficient detection.
 * The setter triggers immediately when window.klaviyo is assigned by the external script.
 *
 * @note Fallback: 5-second timeout ensures events are flushed even if setter doesn't trigger.
 */
export function useKlaviyo() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalAny = typeof window !== 'undefined' ? (window as any) : undefined;
  if (globalAny && !globalAny.__klaviyoQueues) {
    globalAny.__klaviyoQueues = {
      track: [] as Array<{ name: string; payload?: LogParameters }>,
      identify: [] as Array<{ payload?: LogParameters }>,
    };
  }

  const klaviyoIsReady = useCallback((): boolean => {
    return typeof window !== 'undefined' && typeof window.klaviyo?.track === 'function';
  }, []);

  /**
   * Flushes all queued events to Klaviyo
   * @note Only executes when Klaviyo SDK is ready
   */
  const flushQueues = useCallback((): void => {
    if (!klaviyoIsReady()) {
      return;
    }
    const q = globalAny.__klaviyoQueues;
    while (q.identify.length > 0) {
      const item = q.identify.shift();
      if (!item) {
        break;
      }
      window.klaviyo?.identify(item.payload);
    }
    while (q.track.length > 0) {
      const item = q.track.shift();
      if (!item) {
        break;
      }
      if (typeof item.name === 'string' && item.name.trim() !== '') {
        window.klaviyo?.track(item.name, item.payload);
      }
    }
  }, [klaviyoIsReady, globalAny]);

  if (globalAny) {
    globalAny.__klaviyoFlushQueues = flushQueues;
  }

  /**
   * Sets up event listener for Klaviyo script load
   * @note Uses Object.defineProperty to detect when window.klaviyo is set by external script
   * @note Includes 5-second fallback timeout as safety mechanism
   */
  useEffect(() => {
    if (klaviyoIsReady()) {
      flushQueues();
      return;
    }

    if (!globalAny) {
      return;
    }

    let klaviyoValue = globalAny.klaviyo;
    let isListening = true;

    Object.defineProperty(globalAny, 'klaviyo', {
      get() {
        return klaviyoValue;
      },
      set(newValue) {
        klaviyoValue = newValue;
        if (isListening && newValue?.track) {
          flushQueues();
        }
      },
      configurable: true,
    });

    const fallbackTimeout = setTimeout(() => {
      if (klaviyoIsReady()) {
        flushQueues();
      }
    }, 5000);

    return () => {
      isListening = false;
      clearTimeout(fallbackTimeout);
    };
  }, [flushQueues, klaviyoIsReady, globalAny]);

  /**
   * Tracks an event to Klaviyo
   * @note Events are queued if Klaviyo script hasn't loaded yet
   */
  function track(eventName: string, payload?: LogParameters) {
    if (typeof eventName !== 'string' || eventName.trim() === '') {
      return;
    }

    if (!klaviyoIsReady()) {
      globalAny?.__klaviyoQueues?.track.push({ name: eventName, payload });
      return;
    }

    window.klaviyo?.track(eventName, payload);
  }

  /**
   * Identifies a user to Klaviyo
   * @note Identification is queued if Klaviyo script hasn't loaded yet
   */
  function identify(payload?: LogParameters) {
    if (!klaviyoIsReady()) {
      globalAny?.__klaviyoQueues?.identify.push({ payload });
      return;
    }

    window.klaviyo?.identify(payload);
  }

  const isAvailable = klaviyoIsReady();

  return { track, identify, isAvailable } as const;
}
